<template>
  <VDialog v-model="dialogModel" :fullscreen="$vuetify.breakpoint.smAndDown" width="1185">
    <VCard class="">
      <div class="pt-10 px-5 pb-2 pt-md-7" :class="{'white': $vuetify.breakpoint.smAndDown}">
        <div class="interviewers-title">
          <template v-if="preset === 'aiInterview'">
            {{ $t('templateAiInterview') }}
          </template>
          <template>
            {{ preset.title }}
          </template>
          <VBtn
            fab
            icon
            small
            absolute
            top
            right
            class="mt-5 mr-n4"
            @click="dialogController.closeDialog()"
          >
            <VIcon>
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <div class="step-subtitle px-2 px-md-4 mb-md-0 text-center">
          <template v-if="preset === 'aiInterview'">
            {{ $t('templateAiInterviewDescription') }}
          </template>
          <template v-else>
            {{ preset.description }}
          </template>
        </div>
      </div>
      <div class="interviewers-text">
        {{ $t('chooseInterviewer') }}:
      </div>
      <div class="interviewers d-sm-flex">
        <div
          class="interviewers__item slide"
          v-for="interviewer of interviewers"
          :key="interviewer.id"
        >
          <VacancyPresetInterviewersItem
            :interviewer="interviewer"
            :closed="closed"
            :selected-interviewer="selectedInterviewer"
            :limited-preset-questions="limitedPresetQuestions"
            :interviewers-length="interviewersLength"
            :current-avatar="priorityAvatar"
            @selected="selectedInterviewer = $event"
          />
        </div>
      </div>
      <VContainer class="px-7 pa-7 white">
        <VCardActions class="justify-center">
          <VBtn
            outlined
            color="success"
            @click="close()"
            class="mr-1"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            v-if="selectedInterviewer"
            elevation="0"
            large
            color="success"
            :loading="selectLoading"
            :disabled="selectLoading"
            @click="select"
          >
            {{ $t('choose') }}
          </VBtn>
        </VCardActions>
      </VContainer>
    </VCard>
  </VDialog>
</template>

<script>
import VacancyPresetInterviewersItem from '@/components/Vacancy/VacancyPresetInterviewersItem';
import {
  computed, ref, toRefs, watch,
} from '@vue/composition-api';
import useDialog from '@/compositions/useDialog';
import useGlobalEventEmitter from '@/compositions/useGlobalEventEmitter';
import Wizard from '@/orm/models/Wizard/Wizard';
import useAiInterviewQuestions from '@/compositions/useAiInterviewQuestions';
import { interviewPresetTypes } from '@/enums';
import { useI18n } from 'vue-i18n-composable';
import useInterviewer from '@/compositions/useInterviewer';

export default {
  name: 'VacancyPresetInterviewers',
  components: { VacancyPresetInterviewersItem },
  props: {
    preset: {
      type: [String, Object],
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    avatars: {
      type: Object,
      required: true,
    },
    vacancyId: {
      type: Number,
      required: true,
    },
    aiInterviewPresetId: {
      type: Number,
      default: null,
      required: false,
    },
    currentAvatar: {
      type: String,
      default: null,
      required: false,
    },
  },
  setup(props, { emit }) {
    const {
      value, preset, avatars, vacancyId, aiInterviewPresetId, currentAvatar,
    } = toRefs(props);
    const { updateQuestions } = useAiInterviewQuestions();
    const { interviewers: initInterviewers } = useInterviewer();
    const selectedInterviewer = ref(null);
    const closed = ref(false);
    const selectLoading = ref(false);
    const { dialogModel, dialogController } = useDialog({ emit, value });
    const { emitEvent } = useGlobalEventEmitter('interviewers-video');
    const { locale } = useI18n();

    function getPriorityAvatar() {
      if (!vacancyId.value) {
        return currentAvatar.value;
      }
      const hasEditStorage = localStorage.getItem(`vacancy_${vacancyId.value}_avatar_for_edit`);
      if (hasEditStorage) {
        return hasEditStorage;
      }
      return currentAvatar.value;
    }

    const priorityAvatar = ref(getPriorityAvatar());

    const currentInterviewerId = computed(() => (locale.value === 'en' ? 3 : 1));

    const presetQuestionsLength = computed(() => preset.value?.counts?.questions);
    const limitedPresetQuestions = computed(() => {
      return ((preset.value.type === interviewPresetTypes.COMMON
      && (presetQuestionsLength.value < 20)) || (preset.value === 'aiInterview')
        ? 20
        : presetQuestionsLength.value + 5);
    });

    watch(dialogModel, (newValue) => {
      if (newValue) {
        closed.value = false;
      }
    });

    const interviewers = computed(() => {
      let result = [];

      if (preset.value === 'aiInterview') {
        if (avatars.value) {
          const filteredAvatars = Object.entries(avatars.value)
            .filter(([key]) => key !== '$id' && key !== 'id')
            .map(([, val]) => val);
          return filteredAvatars;
        }
        return [];
      }

      const regularInterviewers = initInterviewers.value.filter((i) => i.counts.questions);

      if (regularInterviewers) {
        result = result.concat(regularInterviewers);
      }

      if (avatars.value) {
        const filteredAvatars = Object.entries(avatars.value)
          .filter(([key]) => key !== '$id' && key !== 'id')
          .map(([, val]) => val);
        result = result.concat(filteredAvatars);
      }

      return result;
    });

    const interviewersLength = computed(() => interviewers.value?.length);

    watch(dialogModel, (newValue) => {
      if (!newValue) {
        selectedInterviewer.value = null;
        emitEvent('pause');
      } else {
        selectedInterviewer.value = null;
        priorityAvatar.value = getPriorityAvatar();
      }
    });

    watch([currentAvatar, vacancyId], () => {
      priorityAvatar.value = getPriorityAvatar();
    });

    watch(selectedInterviewer, (newValue) => {
      const hasEditStorage = vacancyId.value && localStorage.getItem(`vacancy_${vacancyId.value}_avatar_for_edit`);
      const isAiInterview = preset.value === 'aiInterview';
      if (hasEditStorage && isAiInterview) {
        if (newValue && newValue.actor_id) {
          priorityAvatar.value = newValue.actor_id;
        } else {
          priorityAvatar.value = getPriorityAvatar();
        }
      }
    });


    selectedInterviewer.value = null;

    async function select() {
      if (selectLoading.value) return;
      selectLoading.value = true;
      try {
        const { actor_id } = selectedInterviewer.value;
      const isAiInterview = preset.value === 'aiInterview';

      if (!actor_id) {
        Wizard.setIsAiInterviewer(false);
        dialogController.closeDialog();
        emitEvent('pause');
        emit('selected', selectedInterviewer.value);
        return;
      }

      const isEditMode = currentAvatar.value !== null && currentAvatar.value !== undefined;
      const hasEditStorage = vacancyId.value && localStorage.getItem(`vacancy_${vacancyId.value}_avatar_for_edit`);
      const isActuallyEditMode = isEditMode && hasEditStorage;
      
      if (isAiInterview && isActuallyEditMode) {
        priorityAvatar.value = actor_id;
        emit('selected', selectedInterviewer.value);
        dialogController.closeDialog();
        return;
      }

      if (vacancyId.value && typeof vacancyId.value === 'number') {
        const storageKey = `vacancy_${vacancyId.value}_avatar_for_create`;
        localStorage.setItem(storageKey, actor_id);
        emit('updateFromLocalStorage');
      }
      
      Wizard.setIsAiInterviewer(true);

      if (isAiInterview) {
        Wizard.setIsAiTemplate(true);
        if (vacancyId.value && typeof vacancyId.value === 'number') {
          if (!Wizard.modelState().vacancyId) {
            Wizard.setVacancyId(vacancyId.value);
          }
          await Wizard.saveWizardState();
        }
        emit('setAiInterviewer', aiInterviewPresetId.value ? 1 : 0);
        emit('selected', selectedInterviewer.value);
        dialogController.closeDialog();
        return;
      }

      if (!isAiInterview) {
        Wizard.setIsAiTemplate(false);
      }

      Wizard.setInterviewerId(currentInterviewerId.value);
      await Promise.all([Wizard.fetchScript(), Wizard.saveWizardState()]);

      const { selectedQuestions } = Wizard.modelState();
      if (selectedQuestions.length < 20 && preset.value.type === interviewPresetTypes.COMMON) {
        emit('selected', initInterviewers.value[0]);
        dialogController.closeDialog();
        return;
      }

      if (vacancyId.value && typeof vacancyId.value === 'number') {
        await updateQuestions(vacancyId.value, {
          vacancyId: vacancyId.value,
          questions: selectedQuestions.map((q) => q.question),
          presetId: preset.value.id,
          avatar: actor_id,
        });
      }
      dialogController.closeDialog();
      emit('setAiInterviewer', 0);
      } finally {
        selectLoading.value = false;
      }
    }

    function close() {
      selectedInterviewer.value = null;
      dialogController.closeDialog();
      closed.value = true;
    }

    return {
      selectedInterviewer,
      dialogModel,
      dialogController,
      interviewers,
      select,
      close,
      closed,
      limitedPresetQuestions,
      interviewersLength,
      priorityAvatar,
      selectLoading,
    };
  },
};
</script>
<style lang="scss" scoped>
.interviewers-title {
  text-align: center;
  font-size: 2.125rem;
  font-weight: bold;
  color: #091A35;
}
.interviewers-text {
  text-align: center;
  font-size: 0.9375rem;
  color: #091A35;
  margin-bottom: 1.5rem;
}
.interviewers {
  background: #F1F6FB;
  padding: 2.5rem 1rem;
  justify-content: center;
  &__item {
    width: 50%;
    max-width: 18.75rem;
    margin-left: 10px;
    flex-shrink: 1;
    &:first-child {
      margin-left: 0;
    }
  }

}
@media #{map-get($display-breakpoints, 'md-and-down')} {
  .interviewers-title {
    font-size: 1.75rem;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .interviewers {
    padding: 1.75rem;
    &__item {
      margin-left: 1.5rem;
    }
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .interviewers-title {
    font-size: 1.25rem;
  }
  .interviewers {
    padding: 1.75rem 1rem 0 1rem;
    &__item {
      width: 100%;
      margin: 0 auto;
      padding-bottom: 1.75rem;
      &:first-child {
        margin-left: auto;
      }
    }
  }
}
</style>
<style lang="sass" scoped>
.video
  width: 100%
  .slide
    width: 270px
  .slide:first-child
    margin-left: 0 !important
  .slider
    min-height: 272px
    &::v-deep
      .v-slide-group__prev
        min-width: 0
        width: 0
        flex: 0
      .v-slide-group__next
        min-width: 0
        width: 0
        flex: 0
</style>

<i18n locale="en" lang="yaml">
  template: Template
  from: from
  chooseInterviewer: 'Questions will be voiced'
  cancel: Cancel
  choose: Choose
  templateAiInterview: Selecting and voicing questions from AI
  templateAiInterviewDescription: The AI will select and voice the questions for your job. You will be able to make changes if necessary.
</i18n>

<i18n locale="ru" lang="yaml">
  template: Шаблон
  from: из
  chooseInterviewer: 'Вопросы будет озвучивать'
  cancel: Отмена
  choose: Выбрать
  templateAiInterview: Подбор и озвучка вопросов от ИИ
  templateAiInterviewDescription: ИИ подберет и озвучит вопросы для вашей вакансии. Вы сможете внести изменения при необходимости.
</i18n>

<!-- TODO
Can ask 5 out of 10 question
ТУТ НАДО переменные прямо в шаблон всунуть, что это за
-->
